import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { useUserLocation } from '../context/LocationContext';
import { toast } from 'sonner';
import { 
  Home, Heart, MessageCircle, Share2, Bookmark, MoreHorizontal,
  Users, Play, Image as ImageIcon, Loader2, RefreshCw, UserPlus, PenSquare,
  Sparkles, TrendingUp, Star, Flame, Zap, Lock, Rocket, Eye, ChevronRight, MapPin
} from 'lucide-react';
import { StoriesBar } from '../components/StoriesBar';
import { SpotlightPostCard } from '../components/SpotlightPostCard';
import SwipeCarousel from '../components/SwipeCarousel';
import SponsoredAd from '../components/SponsoredAd';
import { LiveActivityStrip, RecentActivityFeed, SocialProofLabel } from '../components/LiveActivityStrip';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { FeedInputBar } from '../components/FeedInput';
import { FeedPromoInsert, EmptyStatePromos } from '../components/PromoCard';
import LocalTopCreatorsWidget from '../components/LocalTopCreatorsWidget';

// ─── Momentum Badge ──────────────────────────────────────
function MomentumBadge({ momentum }) {
  if (!momentum) return null;
  const config = {
    blowing_up: { icon: Rocket, label: 'Blowing Up', cls: 'from-red-500 to-orange-500 shadow-[0_0_12px_rgba(239,68,68,0.4)]', anim: 'animate-pulse' },
    trending:   { icon: Flame, label: 'Trending', cls: 'from-orange-500 to-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.3)]', anim: '' },
    hot:        { icon: Zap, label: 'Hot Right Now', cls: 'from-yellow-500 to-amber-400 shadow-[0_0_8px_rgba(234,179,8,0.3)]', anim: '' },
  };
  const c = config[momentum];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span data-testid={`momentum-badge-${momentum}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r ${c.cls} ${c.anim}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

// ─── Social Proof Strip ──────────────────────────────────
function SocialProofStrip({ stats }) {
  if (!stats) return null;
  const items = [
    stats.active_now > 0 && { icon: Eye, text: `${stats.active_now.toLocaleString()} active now`, color: 'text-green-400' },
    stats.reactions_today > 0 && { icon: Flame, text: `${stats.reactions_today.toLocaleString()} reactions today`, color: 'text-orange-400' },
    stats.trending_posts > 0 && { icon: TrendingUp, text: `${stats.trending_posts} trending`, color: 'text-violet-400' },
    stats.new_creators_this_week > 0 && { icon: UserPlus, text: `${stats.new_creators_this_week} new creators this week`, color: 'text-cyan-400' },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div data-testid="social-proof-strip" className="flex items-center gap-4 overflow-x-auto scrollbar-hide py-2 px-1 mb-3">
      {items.map((item, i) => (
        <span key={i} className={`flex items-center gap-1.5 text-xs whitespace-nowrap ${item.color}`}>
          <item.icon className="w-3.5 h-3.5" />
          <span className="font-medium">{item.text}</span>
        </span>
      ))}
    </div>
  );
}

// ─── Creator Momentum Card ───────────────────────────────
function MomentumCard({ momentum }) {
  if (!momentum || !momentum.message) return null;
  return (
    <div data-testid="creator-momentum-card" className="mb-4 p-3 rounded-xl bg-gradient-to-r from-violet-600/10 to-amber-600/10 border border-violet-500/20">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-violet-400" />
        <span className="text-sm font-semibold text-foreground">{momentum.message}</span>
      </div>
      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
        {momentum.today_views > 0 && <span>{momentum.today_views} views today</span>}
        {momentum.today_reactions > 0 && <span>{momentum.today_reactions} reactions</span>}
        {momentum.week_followers > 0 && <span>+{momentum.week_followers} followers this week</span>}
      </div>
    </div>
  );
}

// ─── Micro Engagement Bar ────────────────────────────────
function MicroEngagementBar({ postId, getAuthHeaders }) {
  const actions = [
    { key: 'fire', emoji: '🔥', label: 'This Hard' },
    { key: 'remix', emoji: '🎤', label: 'Remix This' },
    { key: 'support', emoji: '💰', label: 'Support' },
    { key: 'run_it_back', emoji: '🔁', label: 'Run It Back' },
  ];
  const [active, setActive] = useState(null);

  const handleAction = async (action) => {
    try {
      setActive(action);
      await fetch(`${API}/feed/micro-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ post_id: postId, action }),
      });
      setTimeout(() => setActive(null), 1500);
    } catch {
      setActive(null);
    }
  };

  return (
    <div data-testid={`micro-engagement-${postId}`} className="flex items-center gap-1 mt-2 overflow-x-auto scrollbar-hide">
      {actions.map(a => (
        <button
          key={a.key}
          data-testid={`micro-btn-${a.key}-${postId}`}
          onClick={() => handleAction(a.key)}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all duration-200
            ${active === a.key 
              ? 'bg-violet-600/30 border-violet-500/50 text-foreground scale-105' 
              : 'bg-muted/20 border border-border hover:bg-muted/40 text-muted-foreground hover:text-foreground'}`}
        >
          <span>{a.emoji}</span>
          <span className="hidden sm:inline">{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Creator Discovery Card ──────────────────────────────
function DiscoverCreators({ creators, onFollow }) {
  if (!creators || !creators.length) return null;
  return (
    <div data-testid="discover-creators-card" className="mb-4 p-4 rounded-2xl bg-card/50 border border-border">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground/80">You Should Follow</h3>
        <Link to="/artists" className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-0.5">
          See All <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
        {creators.map(c => (
          <div key={c.user_id} className="flex-shrink-0 w-28 text-center group">
            <div className="w-16 h-16 mx-auto rounded-full bg-muted overflow-hidden ring-2 ring-border group-hover:ring-violet-500/50 transition-all mb-1.5">
              {c.avatar ? (
                <img src={c.avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-lg font-bold text-muted-foreground">{(c.name || '?')[0]}</div>
              )}
            </div>
            <p className="text-xs font-semibold truncate text-foreground">{c.name}</p>
            <p className="text-[10px] text-muted-foreground truncate mb-1.5">{c.handle ? `@${c.handle}` : ""}</p>
            <button
              onClick={() => onFollow(c.user_id)}
              data-testid={`discover-follow-${c.user_id}`}
              className="text-[10px] px-3 py-1 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors"
            >
              Follow
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Main Smart Feed Page ─────────────────────────────────
export default function SpotlightFeedPage() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const { isGuest } = useGuest();
  const { location: userLocation, displayLabel, openLocationPicker } = useUserLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(0);
  const [feedFilter, setFeedFilter] = useState('foryou');
  const [feedMessage, setFeedMessage] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Smart feed extras
  const [socialProof, setSocialProof] = useState(null);
  const [creatorMomentum, setCreatorMomentum] = useState(null);
  const [discoverCreators, setDiscoverCreators] = useState([]);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  // ─── Fetch feed ─────────────────────────────────────────
  const fetchFeed = useCallback(async (refresh = false, retries = 2) => {
    if (refresh) {
      setRefreshing(true);
      setCursor(0);
    }

    const headers = getAuthHeaders();
    const offset = refresh ? 0 : cursor;

    // Location params for Phase 3 personalization
    const locParams = [];
    if (userLocation?.city) locParams.push(`city=${encodeURIComponent(userLocation.city)}`);
    if (userLocation?.lat != null && userLocation?.lng != null) {
      locParams.push(`lat=${userLocation.lat}`);
      locParams.push(`lng=${userLocation.lng}`);
    }
    if (feedFilter === 'local') locParams.push('local_only=true');
    const locQS = locParams.length ? `&${locParams.join('&')}` : '';

    try {
      const realFilter = feedFilter === 'local' ? 'foryou' : feedFilter;
      const feedUrl = feedFilter === 'subscriber_perks'
        ? `${API}/feed?limit=20&skip=${offset}&filter=subscriber_perks${locQS}`
        : `${API}/feed/smart?limit=20&cursor=${offset}&filter=${realFilter === 'subscriber_perks' ? 'foryou' : realFilter}${locQS}`;

      const res = await fetch(feedUrl, { headers });
      if (res?.ok) {
        const data = await (feedFilter === 'subscriber_perks' ? safeJson(res).then(r => r?.data || {}) : res.json());
        const list = data.posts || [];

        if (refresh) {
          setPosts(list);
        } else {
          setPosts(prev => {
            const existing = new Set(prev.map(p => p.post_id));
            const unique = list.filter(p => !existing.has(p.post_id));
            return [...prev, ...unique];
          });
        }
        setCursor(data.cursor || offset + list.length);
        setHasMore(data.has_more ?? list.length === 20);
        setFeedMessage(data.message || null);
      } else if (retries > 0) {
        await new Promise(r => setTimeout(r, 1000));
        return fetchFeed(refresh, retries - 1);
      }
    } catch (err) {
      if (retries > 0) {
        await new Promise(r => setTimeout(r, 1000));
        return fetchFeed(refresh, retries - 1);
      }
      console.warn('Feed fetch failed after retries:', err?.message || err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [feedFilter, cursor, getAuthHeaders, userLocation]);

  // ─── Fetch side data ────────────────────────────────────
  const fetchSideData = useCallback(async () => {
    const headers = getAuthHeaders();
    const safeFetch = (url) => fetch(url, { headers }).catch(() => null);
    try {
      const [storiesRes, proofRes, momentumRes, discoverRes] = await Promise.all([
        safeFetch(`${API}/stories/following`),
        safeFetch(`${API}/feed/social-proof`),
        safeFetch(`${API}/creator/momentum`),
        safeFetch(`${API}/discover/creator-suggestions?category=suggested&limit=8`),
      ]);

      if (storiesRes?.ok) {
        const r = await safeJson(storiesRes);
        if (r?.ok) setStories(r.data?.stories || []);
      }
      if (proofRes?.ok) setSocialProof(await proofRes.json());
      if (momentumRes?.ok) setCreatorMomentum(await momentumRes.json());
      if (discoverRes?.ok) {
        const d = await discoverRes.json();
        setDiscoverCreators(d.creators || []);
      }
    } catch {}
  }, [getAuthHeaders]);

  // ─── Initial load ───────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    // Load feed for both guests and authenticated users
    fetchFeed(true);
    if (isAuthenticated) fetchSideData();
  }, [isAuthenticated, authLoading]);

  // Re-fetch on filter change
  useEffect(() => {
    if (!authLoading) {
      setLoading(true);
      fetchFeed(true);
    }
  }, [feedFilter]);

  // ─── Infinite scroll observer ───────────────────────────
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !refreshing) {
          setLoadingMore(true);
          fetchFeed(false);
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(sentinelRef.current);
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [hasMore, loadingMore, refreshing, fetchFeed]);

  // ─── Track view ─────────────────────────────────────────
  const trackView = useCallback((postId) => {
    fetch(`${API}/feed/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ post_id: postId, action: 'view' }),
    }).catch(() => {});
  }, [getAuthHeaders]);

  const handleFollow = async (userId) => {
    try {
      await fetch(`${API}/users/${userId}/follow`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      setDiscoverCreators(prev => prev.filter(c => c.user_id !== userId));
      toast.success('Following!');
    } catch {
      toast.error('Failed to follow');
    }
  };

  // ─── Loading state ──────────────────────────────────────
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
      </div>
    );
  }

  const tabCls = "flex items-center gap-1.5 py-2.5 text-xs font-heading font-bold uppercase tracking-wide data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-600 data-[state=active]:to-violet-500 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl transition-all";

  return (
    <div className="min-h-screen pb-20 bg-background" data-testid="spotlight-feed-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-amber-500 flex items-center justify-center">
                <Sparkles className="w-4.5 h-4.5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-black font-heading uppercase tracking-wide text-foreground">
                  {displayLabel ? `Trending in ${userLocation?.city || userLocation?.neighborhood || 'Your Area'}` : "What's Happening Now!"}
                </h1>
                {displayLabel && (
                  <button
                    onClick={openLocationPicker}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-secondary transition-colors"
                    data-testid="feed-location-btn"
                  >
                    <MapPin className="w-3 h-3" />
                    <span>{displayLabel}</span>
                  </button>
                )}
                {!displayLabel && (
                  <button
                    onClick={openLocationPicker}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-secondary transition-colors"
                    data-testid="feed-set-location-btn"
                  >
                    <MapPin className="w-3 h-3" />
                    <span>Set your area</span>
                  </button>
                )}
              </div>
            </div>
            <Button 
              variant="ghost" size="sm" onClick={() => { setLoading(true); fetchFeed(true); if (isAuthenticated) fetchSideData(); }}
              disabled={refreshing}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
              data-testid="feed-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-3 relative z-10">
        {/* Stories */}
        <StoriesBar stories={stories} currentUserId={user?.user_id} />

        {/* Live Activity Strip */}
        <LiveActivityStrip />

        {/* Post Input */}
        <FeedInputBar onPostCreated={() => { setLoading(true); fetchFeed(true); fetchSideData(); }} />

        {/* Social Proof */}
        <SocialProofStrip stats={socialProof} />

        {/* Creator Momentum */}
        <MomentumCard momentum={creatorMomentum} />

        {/* Tabs */}
        <div className="mb-4">
          <Tabs value={feedFilter} onValueChange={setFeedFilter} className="w-full">
            <TabsList className="w-full grid grid-cols-6 bg-card/50 p-1 rounded-2xl h-auto border border-border">
              <TabsTrigger value="foryou" className={tabCls} data-testid="tab-foryou">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">For You</span>
              </TabsTrigger>
              <TabsTrigger value="local" className={tabCls.replace('from-violet-600', 'from-emerald-600').replace('to-violet-500', 'to-teal-500')} data-testid="tab-local">
                <MapPin className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Local</span>
              </TabsTrigger>
              <TabsTrigger value="following" className={tabCls} data-testid="tab-following">
                <Users className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Following</span>
              </TabsTrigger>
              <TabsTrigger value="trending" className={tabCls} data-testid="tab-trending">
                <Flame className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Hot</span>
              </TabsTrigger>
              <TabsTrigger value="new" className={tabCls} data-testid="tab-new">
                <Zap className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">New</span>
              </TabsTrigger>
              <TabsTrigger value="subscriber_perks" className={tabCls.replace('from-violet-600', 'from-amber-600').replace('to-violet-500', 'to-orange-500')} data-testid="tab-perks">
                <Lock className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Perks</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Local Top Creators widget — show on For You + Local tabs */}
        {(feedFilter === 'foryou' || feedFilter === 'local') && (
          <div className="mb-4">
            <LocalTopCreatorsWidget limit={5} />
          </div>
        )}

        {/* Discover Creators — show in For You tab */}
        {feedFilter === 'foryou' && (
          <DiscoverCreators creators={discoverCreators} onFollow={handleFollow} />
        )}

        {/* Recent Activity Feed — show in For You tab */}
        {feedFilter === 'foryou' && (
          <RecentActivityFeed getAuthHeaders={getAuthHeaders} />
        )}

        {/* Feed Content */}
        {posts.length === 0 && !loading ? (
          <EmptyFeedState filter={feedFilter} message={feedMessage} navigate={navigate} />
        ) : (
          <div className="space-y-4">
            <SponsoredAd placement="feed" />

            {posts.map((post, index) => (
              <React.Fragment key={post.post_id || `post-${index}`}>
                {/* Momentum badge above card */}
                {post.momentum && (
                  <div className="flex items-center gap-2 -mb-2 ml-1">
                    <MomentumBadge momentum={post.momentum} />
                    {post.engagement_score > 0 && (
                      <span className="text-[10px] text-muted-foreground/50">{Math.round(post.engagement_score)} engagement</span>
                    )}
                  </div>
                )}

                <PostCardWrapper post={post} user={user} trackView={trackView} getAuthHeaders={getAuthHeaders} />

                {/* Social proof label */}
                <SocialProofLabel contentType="artist_post" contentId={post.post_id} />

                {/* Micro engagement bar */}
                <MicroEngagementBar postId={post.post_id} getAuthHeaders={getAuthHeaders} />

                {/* Internal Promo Card — every 6 posts */}
                {(index + 1) % 6 === 0 && index < posts.length - 1 && (
                  <FeedPromoInsert position={Math.floor(index / 6)} onPostCreated={() => { setLoading(true); fetchFeed(true); fetchSideData(); }} />
                )}

                {/* "More from this creator" after every 10 posts */}
                {(index + 1) % 10 === 0 && post.author_handle && (
                  <div className="flex items-center justify-center py-2">
                    <Link
                      to={`/u/${post.author_handle}`}
                      data-testid={`more-from-${post.author_handle}`}
                      className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1 px-4 py-1.5 rounded-full bg-violet-600/10 border border-violet-500/20"
                    >
                      More from @{post.author_handle} <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1" />

            {loadingMore && (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              </div>
            )}

            {!hasMore && posts.length > 0 && (
              <div data-testid="feed-end" className="text-center py-8 text-sm text-muted-foreground/50">
                You're all caught up
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Post Card Wrapper (view tracking) ────────────────────
function PostCardWrapper({ post, user, trackView, getAuthHeaders }) {
  const ref = useRef(null);
  const tracked = useRef(false);

  useEffect(() => {
    if (!ref.current || tracked.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked.current) {
          tracked.current = true;
          trackView(post.post_id);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [post.post_id, trackView]);

  return (
    <div ref={ref}>
      <SpotlightPostCard post={post} currentUser={user} />
    </div>
  );
}

// ─── Empty Feed State ─────────────────────────────────────
function EmptyFeedState({ filter, message, navigate }) {
  const content = {
    foryou: { icon: Sparkles, title: 'Your Feed Awaits', msg: message || 'Follow creators to personalize your feed' },
    following: { icon: Users, title: 'No Posts from Following', msg: message || 'Follow artists to see their content here' },
    trending: { icon: Flame, title: 'Nothing Trending Yet', msg: 'Check back soon for hot content from the DMV' },
    new: { icon: Zap, title: 'No New Posts', msg: 'Create the first post of the day!' },
    subscriber_perks: { icon: Lock, title: 'No Exclusive Content', msg: 'Subscribe to creators to unlock perks' },
  }[filter] || { icon: Sparkles, title: 'Empty Feed', msg: 'Start exploring!' };
  const Icon = content.icon;

  return (
    <Card className="bg-card/50 border-border p-8 text-center rounded-2xl" data-testid="empty-feed-state">
      <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-muted flex items-center justify-center">
        <Icon className="w-10 h-10 text-violet-400" />
      </div>
      <h2 className="font-heading font-black text-xl uppercase tracking-wide mb-2 text-foreground">{content.title}</h2>
      <p className="text-sm text-muted-foreground mb-6">{content.msg}</p>
      <div className="flex gap-3 justify-center flex-wrap">
        <Button onClick={() => navigate('/artists')} className="bg-violet-600 hover:bg-violet-500 text-white font-heading uppercase text-sm">
          Discover Artists
        </Button>
        <Button variant="outline" onClick={() => navigate('/post/create')} className="border-border text-foreground font-heading uppercase text-sm">
          <PenSquare className="w-4 h-4 mr-1.5" /> Create Post
        </Button>
      </div>
      <EmptyStatePromos />
    </Card>
  );
}
